import * as turf from "@turf/turf";
import type { Feature, MultiPolygon, Polygon } from "geojson";
import { RegistroFincaBorradorLote } from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";

/** Ancho medio mínimo, en metros, de una parte del área que no cubre ningún
 *  lote para mostrarla como pieza. Lo más angosto que deja la unión son los
 *  puentes entre polígonos separados (~2 m, ver `AoiPolygonUnion`), que no
 *  son un polígono del usuario. Un lote real no mide menos de 3 m de ancho. */
const ANCHO_MINIMO_PIEZA_M = 3;

type PoligonoOMulti = Feature<Polygon | MultiPolygon>;

/** Piezas del paso 3 a partir de lo que hay en la base: cada grupo de lotes
 *  que se tocan, fundido en una sola pieza (un polígono ya subdividido se ve
 *  como el polígono que era), más las partes del área que no cubre ningún
 *  lote (polígonos agregados después y todavía no confirmados), sin los
 *  puentes. Sin lotes, el área entera como una pieza. */
export function fx_piezasDesdeLotes(
  lotes: readonly RegistroFincaBorradorLote[],
  aoi: Polygon | null,
): Polygon[] {
  if (lotes.length === 0) return aoi ? [aoi] : [];

  const union = unirTodo(lotes.map((l) => l.areaGeograficaPolygon));
  if (!union) {
    // La unión no resolvió: se muestra el área entera, como antes.
    return aoi ? [aoi] : lotes.map((l) => l.areaGeograficaPolygon);
  }

  const piezas = partes(union);
  if (aoi) {
    try {
      const resto = turf.difference(
        turf.featureCollection([turf.polygon(aoi.coordinates), union]),
      ) as PoligonoOMulti | null;
      if (resto) {
        piezas.push(
          ...partes(resto)
            .flatMap(sinPuentes)
            .filter((p) => anchoMedio(p) >= ANCHO_MINIMO_PIEZA_M),
        );
      }
    } catch {
      // Sin diferencia: solo las piezas de los lotes.
    }
  }
  return piezas;
}

/** Los **polígonos padre** de un conjunto de polígonos: cada grupo de los que
 *  se tocan, fundido en uno solo. Es el perímetro exterior de cada bloque
 *  contiguo — lo que el usuario dibujó (o lo que el archivo representa) ANTES
 *  de subdividirlo en lotes.
 *
 *  Devuelve varios cuando el conjunto tiene bloques separados: un archivo con
 *  dos o tres predios sueltos da dos o tres padres, no uno. Para juntarlos en
 *  UNA geometría (el `aoi_satelite`, que es una sola columna `Polygon`) está
 *  `fx_calcularPerimetroGlobal`, que además tiende puentes entre los bloques;
 *  acá no, justamente porque los bloques son lo que se quiere distinguir.
 *
 *  Los huecos interiores se conservan: si los lotes rodean una laguna, esa
 *  laguna no es parte del predio y el padre la mantiene como agujero.
 *
 *  Si la unión falla (geometría inválida), devuelve los polígonos tal cual:
 *  es peor no mostrar nada que mostrarlos sin agrupar. */
/** ⚠️ **Sin llamadores desde 2026-10-09.** El padre lo calcula el servidor:
 *  al importar un KML (`PoligonosPadreGeometria`) y en cada cambio de lote del
 *  paso 4 (`RecalcularAreaDesdeLotesAsync`). Esta versión con turf corría en
 *  el hilo principal y congelaba la app unos dos segundos con un registro
 *  importado. Se conserva como la referencia del algoritmo —el port de C# es
 *  línea por línea de acá—; si va a volver a usarse, medir primero. */
export function fx_poligonosPadreDe(poligonos: readonly Polygon[]): Polygon[] {
  if (poligonos.length === 0) return [];
  const union = unirTodo(poligonos);
  return union ? partes(union) : [...poligonos];
}

/** Une todos los polígonos en uno (o en un `MultiPolygon`, si hay bloques
 *  separados). `null` si turf no pudo. */
function unirTodo(poligonos: readonly Polygon[]): PoligonoOMulti | null {
  let union: PoligonoOMulti | null = null;
  try {
    for (const poligono of poligonos) {
      const feature = turf.polygon(poligono.coordinates);
      union = union
        ? (turf.union(
            turf.featureCollection([union, feature]),
          ) as PoligonoOMulti | null)
        : feature;
    }
  } catch {
    return null;
  }
  return union;
}

/** Radio, en metros, de la apertura que quita los puentes: un poco más que
 *  el medio ancho de un puente (~1 m), así desaparecen enteros. */
const RADIO_APERTURA_M = 1.5;

/** Tolerancia, en grados (~0.3 m), para simplificar la pieza abierta: la
 *  apertura redondea las esquinas con arcos de muchos vértices, que no
 *  aportan forma y que al redondear a 7 decimales pueden cruzarse. */
const TOLERANCIA_SIMPLIFICADO_GRADOS = 0.3 / 111320;

/** Quita de una parte del área los puentes que la unían a otros polígonos
 *  (`AoiPolygonUnion`): tiras de ~2 m de ancho que no son del usuario. Se
 *  hace con una apertura morfológica (encoger y volver a crecer
 *  `RADIO_APERTURA_M`), que elimina todo lo más angosto que eso, y se
 *  simplifica el resultado. El costo: las esquinas convexas quedan
 *  redondeadas en menos de 1 m. Solo se usa para las partes del área que
 *  todavía no son lotes (polígonos agregados y sin confirmar al retomar),
 *  así que no toca ninguna geometría ya confirmada. Si al quitar un puente
 *  la parte queda en dos, devuelve cada una. */
function sinPuentes(parte: Polygon): Polygon[] {
  try {
    const feature = turf.polygon(parte.coordinates);
    const encogida = turf.buffer(feature, -RADIO_APERTURA_M, { units: "meters" });
    // Todo era más angosto que un puente: no hay pieza.
    if (!encogida) return [];
    const abierta = turf.buffer(encogida, RADIO_APERTURA_M, { units: "meters" });
    if (!abierta) return [];
    const simplificada = turf.simplify(abierta as PoligonoOMulti, {
      tolerance: TOLERANCIA_SIMPLIFICADO_GRADOS,
      highQuality: true,
    }) as PoligonoOMulti;
    return partes(simplificada).flatMap(sinNudos);
  } catch {
    return [parte];
  }
}

/** Restar un puente puede dejar un hilo de ancho cero donde se unía, y el
 *  contorno queda tocándose a sí mismo (un "nudo" que la depuración
 *  descartaría como error topológico). `unkinkPolygon` lo separa en
 *  polígonos simples; los hilos resultantes no tienen ancho y los filtra
 *  `ANCHO_MINIMO_PIEZA_M` más adelante. */
function sinNudos(parte: Polygon): Polygon[] {
  try {
    const feature = turf.cleanCoords(turf.polygon(parte.coordinates)) as Feature<Polygon>;
    if (turf.kinks(feature).features.length === 0) return [feature.geometry];
    return turf.unkinkPolygon(feature).features.map((f) => f.geometry);
  } catch {
    return [parte];
  }
}

function partes(feature: PoligonoOMulti): Polygon[] {
  return feature.geometry.type === "Polygon"
    ? [feature.geometry]
    : feature.geometry.coordinates.map((coordinates) => ({
        type: "Polygon" as const,
        coordinates,
      }));
}

/** Ancho medio en metros: 2 × área / perímetro. */
function anchoMedio(polygon: Polygon): number {
  try {
    const area = turf.area(polygon);
    const perimetro = turf.length(turf.polygonToLine(polygon) as Feature, {
      units: "meters",
    });
    return perimetro > 0 ? (2 * area) / perimetro : 0;
  } catch {
    return 0;
  }
}
